// web/src/tutorial/quickstart/key-ripple.ts
// Key Ripple · Quick Start（最短流程：选演奏者 → 选 MIDI → 生成动画）
// 依据：H:\key_ripple_rust
//   src/ui/app.rs（页签结构）、avatar_panel.rs / avatar_display.rs、midi_panel.rs、
//   execution_panel.rs、data.rs（默认值）、asset/i18n/zh-CN.json 与 en-US.json（界面原文）
//
// 写作原则（作者要求）：这一页只讲「怎么用默认参数跑通」，参数细节全部下沉到
// /details/key-ripple.html（软件界面详细说明）。

import { l } from "../l";
import type { TutorialDoc } from "../types";

export const keyRippleQuickstart: TutorialDoc = {
  key: "key_ripple",
  slug: "key-ripple",
  kind: "quickstart",
  icon: "🎹",
  title: l("Key Ripple 钢琴演奏动画", "Key Ripple Piano Performance"),
  subtitle: l(
    "最短的一条路：选一个演奏者，选一份 MIDI，点生成，然后在 Blender 或 Unreal 里加载结果。全程不需要改任何参数。",
    "The shortest path: pick a performer, pick a MIDI file, hit generate, then load the result in Blender or Unreal. Nothing needs tuning along the way.",
  ),
  readMinutes: 6,
  video: {
    url: "https://www.bilibili.com/video/BV1HhSqBhEbw/",
    youtube: "https://www.youtube.com/watch?v=Q4gSBsoMV5k",
    caption: l(
      "Key Ripple 完整操作演示：2.keyRipple使用演示视频（B 站）",
      "Key Ripple full walkthrough: How to use KeyRipple (YouTube)",
    ),
    series: [
      { title: l("1.keyRipple软件介绍", "From Midi to Animation: KeyRipple Introduce"), url: "https://www.bilibili.com/video/BV1A4SqBPEAY/", youtube: "https://www.youtube.com/watch?v=M0-WVsCM66k" },
      { title: l("钢琴动画软件KeyRipple 开放免费下载及使用", "钢琴动画软件KeyRipple 开放免费下载及使用"), url: "https://www.bilibili.com/video/BV1ms7A6eEo7/" },
      { title: l("钢琴动画生成软件 KeyRipple 简介", "Master 3D Piano Performance Animation with KeyRipple | Complete Blender & Unreal Engine Guide"), url: "https://www.bilibili.com/video/BV1icG36sEuB/", youtube: "https://www.youtube.com/watch?v=GMIgjdpK4SQ" },
    ],
  },
  images: [
    {
      src: "/assets/image/products/key-ripple/CN/角色设置.png",
      srcEn: "/assets/image/products/key-ripple/EN/Avatar.png",
      label: l("角色设置", "Avatar"),
      width: 1002,
      height: 955,
    },
    {
      src: "/assets/image/products/key-ripple/CN/MIDI处理.png",
      srcEn: "/assets/image/products/key-ripple/EN/MIDI.png",
      label: l("MIDI 处理", "MIDI"),
      width: 1002,
      height: 955,
    },
    {
      src: "/assets/image/products/key-ripple/CN/执行操作.png",
      srcEn: "/assets/image/products/key-ripple/EN/Execute.png",
      label: l("执行操作", "Execute"),
      width: 1002,
      height: 955,
    },
  ],
  sections: [
    // ─────────────────────────────────────────────────────────────
    {
      id: "overview",
      title: l("这一篇要做什么", "What you will do"),
      image: { image: 0 },
      blocks: [
        {
          kind: "callout",
          tone: "tip",
          title: l("先拿到软件", "Get the software first"),
          text: l(
            "Key Ripple 可以**免费使用** —— 到[下载页](/free/download.html)拿 `key_ripple_dist.zip` 解压即用，装好就是免费版，**不需要授权码**，内置演奏者可以直接跑完这一篇的流程。想自定义角色、开启更多功能，再购买完整版。",
            "Key Ripple is **free to use** — grab `key_ripple_dist.zip` from the [download page](/free/download.html) and unzip it. It runs as the free edition with **no license key**, and the bundled performers can run the whole walkthrough below. Custom performers and more features are available in the full version.",
          ),
        },
        {
          kind: "p",
          text: l(
            "Key Ripple 把一份 MIDI 变成钢琴演奏动画。你只要告诉它两件事：**谁来弹**（演奏者）和**弹什么**（MIDI 文件）。剩下的——每根手指按哪个键、手掌怎么移动、身体朝哪边转——它自己算，最后导出一份 Blender / Unreal 插件能直接加载的动画数据。你不需要在 3D 软件里手动摆任何一帧。",
            "Key Ripple turns a MIDI file into a piano performance animation. You tell it two things: **who plays** (the performer) and **what to play** (the MIDI file). Everything else — which finger presses which key, how the palm travels, which way the body turns — is computed for you, and it exports animation data the Blender / Unreal add-on loads directly. You never hand-key a frame.",
          ),
        },
        {
          kind: "p",
          text: l(
            "整件事就是三步，按界面顶部的三个页签从左往右走：",
            "It comes down to three steps, in the order of the three tabs along the top:",
          ),
        },
        {
          kind: "steps",
          items: [
            {
              title: l("`⚙ 角色设置`：选演奏者", "`⚙ 角色设置` / Avatar: pick the performer"),
              text: l(
                "在下拉菜单里挑一个自带的演奏者，下面的动作参数保持默认。",
                "Choose one of the bundled performers from the dropdown and leave the motion parameters at their defaults.",
              ),
            },
            {
              title: l("`🎵 MIDI处理`：选 MIDI", "`🎵 MIDI处理` / MIDI: load the MIDI"),
              text: l(
                "选 MIDI 文件、点扫描、勾出要生成动画的那条轨道。",
                "Select the MIDI file, scan it, and tick the track you want animated.",
              ),
            },
            {
              title: l("`▶ 执行操作`：生成动画", "`▶ 执行操作` / Execute: generate"),
              text: l(
                "点 `⚡ 一键执行`，等右侧控制台跑完，拿到结果文件。",
                "Click `⚡ Run All`, let the console finish, and collect the result file.",
              ),
            },
          ],
        },
        {
          kind: "callout",
          tone: "tip",
          text: l(
            "第一次跑，用一首**单轨钢琴独奏**的 MIDI 最容易一次成功。确认整条链路通了，再换成自己的曲子。",
            "For your first run, a **single-track solo piano** MIDI is the easiest way to succeed on the first try. Get the whole chain working, then switch to your own material.",
          ),
        },
      ],
    },

    // ─────────────────────────────────────────────────────────────
    {
      id: "tuning",
      title: l("想调参数？看详细说明", "Need to tune something?"),
      image: { image: 0 },
      blocks: [
        {
          kind: "p",
          text: l(
            "这一篇只讲怎么用默认参数把流程跑通，所以刻意不提任何参数。软件界面上每一个可调的东西——角色页的动作参数、MIDI 页的通道 / FPS / 量化、执行页与控制台、记录查看页的筛选——都写在了[软件界面详细说明](/details/key-ripple.html)里，逐个说明它管什么、什么时候该动、往大调还是往小调。建议先用这一篇跑通一次，动画能正常播放了，再回那边挑想动的地方：默认参数对大多数曲子已经够用。",
            "This page sticks to the default settings, so it deliberately skips every parameter. Everything adjustable in the app — the motion parameters on the performer tab, channel / FPS / quantization on the MIDI tab, the execute tab and its console, the filters in the record viewer — is documented one by one in the [UI reference](/details/key-ripple.html): what it controls, when to touch it, and which way to move it. Get one run working here first, confirm the animation plays, and only then go back and pick what to adjust — the defaults already work for most pieces.",
          ),
        },
      ],
    },

    // ─────────────────────────────────────────────────────────────
    {
      id: "prepare",
      title: l("开始之前：准备什么", "Before you start"),
      image: { image: 0 },
      blocks: [
        {
          kind: "p",
          text: l(
            "真正需要准备的只有一份 MIDI 文件，扩展名是 `.mid` 或 `.midi`，放在硬盘哪里都行——程序里手动选，软件只读取它，不会改动你的原文件。演奏者由软件自带，启动后在 `⚙ 角色设置` 的下拉菜单里直接选，不需要自己导入任何模型。",
            "The only thing you need is a MIDI file with a `.mid` or `.midi` extension, and it can live anywhere on disk — you pick it inside the app, which only reads it and never modifies your original. Performers ship with the app: just pick one from the dropdown on `⚙ 角色设置` / Avatar, with no models to import yourself.",
          ),
        },
        {
          kind: "callout",
          tone: "tip",
          text: l(
            "想把你自己的人物做成可用演奏者，是另一套流程，见《自定义人物进阶教程》。",
            "Turning your own character into a usable performer is a separate workflow, covered by the Custom Performer tutorial.",
          ),
        },
      ],
    },

    // ─────────────────────────────────────────────────────────────
    {
      id: "ui-overview",
      title: l("界面总览：四个页签", "The four tabs"),
      image: { image: 0 },
      blocks: [
        {
          kind: "p",
          text: l(
            "程序只有四个页签，前三个构成主流程，顺序是固定的。第四个是排查工具，跑通流程用不到它。",
            "The app has exactly four tabs. The first three form the main flow, in a fixed order. The fourth is a debugging tool you do not need for a normal run.",
          ),
        },
        {
          kind: "table",
          head: [
            l("页签", "Tab"),
            l("干什么", "What it does"),
            l("这轮要不要用", "Needed?"),
          ],
          rows: [
            [
              l("`⚙ 角色设置`", "`⚙ 角色设置` / Avatar"),
              l("选演奏者，查看它的键域与手位。", "Choose the performer and inspect its key range and hand positions."),
              l("要，第 1 步", "Yes — step 1"),
            ],
            [
              l("`🎵 MIDI处理`", "`🎵 MIDI处理` / MIDI"),
              l("选 MIDI 文件、扫描轨道、勾出要生成动画的轨道。", "Load the MIDI, scan its tracks, tick the track to animate."),
              l("要，第 2 步", "Yes — step 2"),
            ],
            [
              l("`▶ 执行操作`", "`▶ 执行操作` / Execute"),
              l("确认设置没选错，然后生成动画。", "Confirm the settings, then generate."),
              l("要，第 3 步", "Yes — step 3"),
            ],
            [
              l("`📋 记录查看`", "`📋 记录查看` / Records"),
              l("逐帧检查生成出来的手部记录，出问题时才用。", "Inspect the generated hand records frame by frame — only when something looks wrong."),
              l("不用", "No"),
            ],
          ],
        },
        {
          kind: "callout",
          tone: "warn",
          text: l(
            "顺序不要颠倒。生成动画要同时用到「演奏者」和「MIDI 轨道」，所以两边都得准备好再去 `▶ 执行操作`；执行页上没有任何地方能改角色或 MIDI。",
            "Do not reorder them. Generation needs both the performer and the MIDI tracks, so get both ready before you open `▶ 执行操作` / Execute; that tab has no input for either one.",
          ),
        },
      ],
    },

    // ─────────────────────────────────────────────────────────────
    {
      id: "performer",
      title: l("第 1 步：选演奏者", "Step 1 — pick the performer"),
      image: { image: 0 },
      blocks: [
        {
          kind: "p",
          text: l(
            "切到 `⚙ 角色设置`。左上角 `角色选择` 卡片里有一个下拉菜单，点开就是软件自带（或你之前登记过）的全部演奏者，选一个即可。",
            "Open `⚙ 角色设置` / Avatar. The `角色选择` / Avatar Selection card in the top-left holds a dropdown listing every performer that ships with the app (plus any you have registered), so just pick one.",
          ),
        },
        {
          kind: "p",
          text: l(
            "选完之后，下拉菜单下面会显示这个演奏者的三个数字：`钢琴键域`、`默认手位`、`单手跨度`。它们只是让你确认「选的是对的人」，不需要你改。",
            "Once selected, three numbers appear under the dropdown: `钢琴键域` / Key range, `默认手位` / Default hand position and `单手跨度` / Hand span. They are there so you can confirm you picked the right performer — nothing to edit.",
          ),
        },
        {
          kind: "p",
          text: l(
            "页面下半部分的 `动作参数` 有七个可调项（`抬掌幅度`、`按键耗时`、`手掌八度移动耗时`、`最大过渡时长`、`抬指时间`、`转向敏感度`、`速度限制剔除`）。**这一篇一个都不用动**，默认值就能算出可用的动作。",
            "The lower half of the page, `动作参数` / Motion Parameters, holds seven adjustable items (`抬掌幅度` / Hand lift amplitude, `按键耗时` / Key press duration, `手掌八度移动耗时` / Octave move duration, `最大过渡时长` / Max transition duration, `抬指时间` / Lift time, `转向敏感度` / Steering sensitivity, `速度限制剔除` / Speed limit culling). **You do not need to touch any of them here** — the defaults already produce usable motion.",
          ),
        },
        {
          kind: "callout",
          tone: "tip",
          text: l(
            "想调动作手感——抬手的幅度、手指按下的快慢、身体转向的频繁程度——去[软件界面详细说明](/details/key-ripple.html)的角色设置一节。",
            "If you want to shape the feel — how high the palm lifts, how fast fingers press, how often the body turns — see the Avatar tab section of the [UI reference](/details/key-ripple.html).",
          ),
        },
      ],
    },

    // ─────────────────────────────────────────────────────────────
    {
      id: "midi",
      title: l("第 2 步：选 MIDI 并勾出轨道", "Step 2 — load the MIDI and tick a track"),
      image: { image: 1 },
      blocks: [
        {
          kind: "p",
          text: l(
            "切到 `🎵 MIDI处理`。左栏管「用哪个文件」，右栏管「生成哪些轨」。四个动作按顺序做完就行：",
            "Open `🎵 MIDI`. The left column decides which file to use, the right column decides which tracks get animated. Four clicks in order:",
          ),
        },
        {
          kind: "ol",
          items: [
            l(
              "点左栏的 `📂 选择 MIDI 文件`，选中你的 MIDI；也可以把文件直接拖到左栏下方的虚线区域 `📥 将 MIDI 文件拖放到此处`。",
              "Click `📂 选择 MIDI 文件` / Select MIDI File in the left column, or drag the file onto the dashed `📥 将 MIDI 文件拖放到此处` / Drop MIDI file here area below it.",
            ),
            l(
              "点 `🔍 扫描 MIDI 信息`。右栏 `轨道选择` 会列出这份 MIDI 里所有**含音符**的轨道，每条给出 `轨道` 号、`通道`、`乐器` 和 `音符数`。",
              "Click `🔍 扫描 MIDI 信息` / Scan MIDI Info. The `轨道选择` / Track Selection table on the right lists every track that **contains notes**, with its `轨道` / Track number, `通道` / Channel, `乐器` / Instrument and `音符数` / Notes.",
            ),
            l(
              "在要生成动画的那条轨道前面打勾。大多数情况下就是 `Acoustic Grand Piano` 那一条——`乐器` 和 `音符数` 两列可以帮你确认。",
              "Tick the track you want animated — usually the `Acoustic Grand Piano` row; the `乐器` / Instrument and `音符数` / Notes columns tell you which one that is.",
            ),
            l(
              "点 `✅ 添加选中的轨道`。左栏 `参数设置` 里的 `已选择的轨道` 会立刻列出你勾的轨道号。",
              "Click `✅ 添加选中的轨道` / Add Selected Tracks. Back in `参数设置` / Parameter Settings on the left, `已选择的轨道` / Selected tracks immediately shows the track numbers you ticked.",
            ),
          ],
        },
        {
          kind: "callout",
          tone: "warn",
          text: l(
            "只打勾是**不生效**的，必须点一次 `✅ 添加选中的轨道`。忘了点，执行时会看到「请先选择至少一个轨道」。",
            "Ticking alone does **nothing** — you must click `✅ 添加选中的轨道` / Add Selected Tracks. Skip it and generation will complain that no track was selected.",
          ),
        },
        {
          kind: "callout",
          tone: "tip",
          text: l(
            "左栏 `参数设置` 里的 `使用通道`、`FPS`、`音符量化` 三个参数保持默认就好。它们各自管什么、什么时候该改，见[软件界面详细说明](/details/key-ripple.html)。",
            "Leave `使用通道` / Use channel, `FPS` and `音符量化` / Note quantization in `参数设置` / Parameter Settings at their defaults. What each one controls and when to change it is covered in the [UI reference](/details/key-ripple.html).",
          ),
        },
        {
          kind: "note",
          text: l(
            "一份 MIDI 把钢琴和伴奏分在不同轨道里时，只勾钢琴那条。勾了鼓轨或铺底轨，动作就会按那条轨的音符来算。",
            "If a MIDI splits piano and accompaniment across tracks, tick only the piano one. Tick a drum or pad track and the motion will be computed from those notes instead.",
          ),
        },
      ],
    },

    // ─────────────────────────────────────────────────────────────
    {
      id: "execute",
      title: l("第 3 步：点生成", "Step 3 — generate"),
      image: { image: 2 },
      blocks: [
        {
          kind: "p",
          text: l(
            "切到 `▶ 执行操作`。左边上面两张卡片（`角色概览`、`MIDI 参数`）只是**回显**你的设置，在这里改不了任何东西；下面是按钮，右边是 `控制台输出`。",
            "Open `▶ 执行操作` / Execute. The two cards at the top of the left column, `角色概览` / Avatar Overview and `MIDI 参数` / MIDI Parameters, are **read-only echoes** of your settings; the buttons sit below them and `控制台输出` / Console Output fills the right column.",
          ),
        },
        {
          kind: "ol",
          items: [
            l(
              "扫一眼回显区：`角色概览` 里有角色名和 `单手音域`，`MIDI 参数` 里有文件名、`轨道`、`通道`、`帧率`。对得上就继续。",
              "Glance at the echoes: `角色概览` / Avatar Overview shows the performer name and `单手音域` / Single-hand range, while `MIDI 参数` / MIDI Parameters shows the file name, `轨道` / Track, `通道` / Channel and `帧率` / Frame rate. If they match, carry on.",
            ),
            l(
              "点 `⚡ 一键执行`。它会一次性串起初始化、指法、动画三步。（想分步排查就点 `🎼 1. 生成指法`，完成后再点 `🎹 2. 生成动画`。）",
              "Click `⚡ 一键执行` / Run All — it chains initialization, fingering and animation in one go. (To debug step by step, click `🎼 1. 生成指法` / Generate Fingering first, then `🎹 2. 生成动画` / Generate Animation.)",
            ),
            l(
              "盯着右边控制台。跑完会出现 `动画生成完成`，以及一行 `KeyRipple配置文件已保存到: …` 的结果路径；顶部状态徽标回到 `状态：空闲`。",
              "Watch the console. When it finishes you get `动画生成完成` / Animation generation complete plus a line with the saved result path; the status badge at the top returns to `状态：空闲` / Status: Idle.",
            ),
          ],
        },
        {
          kind: "callout",
          tone: "warn",
          text: l(
            "如果回显区写着 `未选择角色` 或 `未选择 MIDI 文件`，别急着点生成——回对应的页签补齐，否则只会在控制台留下一条错误。",
            "If an echo card reads `未选择角色` / No avatar selected or `未选择 MIDI 文件` / No MIDI file selected, do not hit generate — go back and fill it in, or the console will only print an error.",
          ),
        },
        {
          kind: "note",
          text: l(
            "生成期间按钮下方会出现转圈和 `正在执行...`，旁边有 `取消`。曲子越长等得越久，控制台会持续刷新进度，不用怀疑它卡住了。",
            "While it runs, a spinner and `正在执行...` / Running... appear under the buttons with a `取消` / Cancel next to them. Longer pieces take longer; the console keeps refreshing, so you can tell it is not stuck.",
          ),
        },
      ],
    },

    // ─────────────────────────────────────────────────────────────
    {
      id: "result",
      title: l("生成了什么、在哪里、怎么用", "What you get and where it goes"),
      image: { image: 2 },
      blocks: [
        {
          kind: "p",
          text: l(
            "程序在自己的 `output/` 目录下写出几个文件。真正要交给插件的是 **`.keyripple`** 这一个：",
            "The app writes several files under its own `output/` folder. The one you hand to the add-on is the **`.keyripple`** file:",
          ),
        },
        {
          kind: "p",
          text: l(
            "程序在自己的 `output/` 目录下写出几个文件，真正要交给插件的只有一个：**`output/final_result/{midi}_{轨道}_{角色}.keyripple`**。它本身很小，里面记着配套三个数据文件（动画、琴键动画、活动曲线）的**绝对路径**，这四个文件要一直待在一起。",
            "The app writes several files under its own `output/` folder, and exactly one of them goes to the add-on: **`output/final_result/{midi}_{track}_{performer}.keyripple`**. It is small and stores the **absolute paths** of three companion data files — the animation, the key animation and the activity curve — so those four files need to stay together.",
          ),
        },
        {
          kind: "p",
          text: l(
            "另外还有一份 `output/hand_recorders/{midi}_{轨道}.hand` 手部记录，只有 `📋 记录查看` 页会读它，插件不读，排查指法问题时才有用。",
            "There is also a hand record at `output/hand_recorders/{midi}_{track}.hand`, read only by the `📋 记录查看` / Records tab — the add-on ignores it, and you only need it when debugging fingering.",
          ),
        },
        {
          kind: "callout",
          tone: "warn",
          text: l(
            "不要搬动 `output/` 目录。`.keyripple` 里记的是另外三个文件的绝对路径，目录一改名或搬走，插件就找不到数据了——只能重新生成。",
            "Do not move the `output/` folder. The `.keyripple` file stores absolute paths to the other three files; rename or move the folder and the add-on can no longer find them, so you would have to regenerate.",
          ),
        },
        {
          kind: "ol",
          items: [
            l(
              "在 Blender 或 Unreal 里启用免费的 MusicDoll 插件，打开钢琴（KeyRipple）演奏者面板。",
              "Enable the free MusicDoll add-on in Blender or Unreal and open its piano (KeyRipple) performer panel.",
            ),
            l(
              "在面板里选中与生成时**同一个**演奏者。",
              "Select the **same** performer you used for generation.",
            ),
            l(
              "加载这份 `.keyripple` 文件，插件会把三个数据文件写回时间线，之后就能播放或渲染。",
              "Load the `.keyripple` file; the add-on writes the three data files onto the timeline, ready to play or render.",
            ),
          ],
        },
        {
          kind: "callout",
          tone: "tip",
          text: l(
            "懒得翻目录的话，控制台顶部的 `复制结果` 会把最近一次生成的结果路径复制到剪贴板，直接粘到插件的文件框里。插件里每个按钮的含义见《插件按钮手册》。",
            "If you would rather not dig through folders, `复制结果` / Copy Result at the top of the console copies the most recent result path to the clipboard — paste it straight into the add-on's file field. See the Plugin Button Manual for what each add-on button does.",
          ),
        },
      ],
    },

    // ─────────────────────────────────────────────────────────────
    {
      id: "checklist",
      title: l("点生成前的自检清单", "Pre-flight checklist"),
      image: { image: 2 },
      blocks: [
        {
          kind: "p",
          text: l(
            "跑之前对着这张表扫一眼，绝大多数「怎么也生成不出来」都能提前发现：",
            "Run through this list before you hit generate — it catches almost every “why won't it generate” case:",
          ),
        },
        {
          kind: "table",
          head: [l("检查项", "Check"), l("应该是什么样", "What it should look like")],
          rows: [
            [
              l("演奏者", "Performer"),
              l(
                "`⚙ 角色设置` 的下拉菜单里选中了一个角色，下方信息框能看到 `钢琴键域` 和 `默认手位`。",
                "A performer is selected on `⚙ 角色设置` / Avatar, and its `钢琴键域` / Key range and `默认手位` / Default hand position show up in the info box.",
              ),
            ],
            [
              l("MIDI 文件", "MIDI file"),
              l(
                "`🎵 MIDI处理` 左栏 `当前文件:` 后面显示的是你要用的那份文件。",
                "The `当前文件:` / Current file line in the left column of `🎵 MIDI` shows the file you meant to use.",
              ),
            ],
            [
              l("轨道", "Track"),
              l(
                "`参数设置` 里的 `已选择的轨道:` 列出了轨道号，而不是 `无轨道`。",
                "`已选择的轨道:` / Selected tracks in `参数设置` / Parameter Settings lists track numbers rather than `无轨道` / No tracks.",
              ),
            ],
            [
              l("执行页回显", "Execute echoes"),
              l(
                "`角色概览` 和 `MIDI 参数` 里没有 `未选择角色` / `未选择 MIDI 文件` 这样的提示。",
                "Neither `角色概览` / Avatar Overview nor `MIDI 参数` / MIDI Parameters shows `未选择角色` / No avatar selected or `未选择 MIDI 文件` / No MIDI file selected.",
              ),
            ],
            [
              l("控制台", "Console"),
              l(
                "跑完能看到 `动画生成完成`，并且给出一行 `.keyripple` 结果文件的保存路径。",
                "When it finishes you see `动画生成完成` / Animation generation complete plus a line with the saved `.keyripple` path.",
              ),
            ],
          ],
        },
        {
          kind: "callout",
          tone: "tip",
          title: l("换过演奏者就重启程序", "Restart after switching performers"),
          text: l(
            "程序只在第一次执行时读取演奏者的键域和手位。中途换了角色再生成，动作还是会按旧角色算（控制台会提示「系统已初始化，如需重新初始化请先重置」）。换角色后重启软件再生成。",
            "The app reads the performer's key range and hand positions only on the first run. Switch performers mid-session and the motion still follows the old one (the console warns that the system is already initialized). Restart the app after switching, then generate.",
          ),
        },
      ],
    },

    // ─────────────────────────────────────────────────────────────
    {
      id: "faq",
      title: l("常见问题", "FAQ"),
      image: { image: 2 },
      blocks: [
        {
          kind: "p",
          text: l(
            "**生成动画会改到我的 MIDI 原文件吗？** 不会。程序只读取 MIDI，所有结果都写到自己的 `output/` 目录里。",
            "**Will generation modify my original MIDI?** No. The app only reads the MIDI; every output goes into its own `output/` folder.",
          ),
        },
        {
          kind: "p",
          text: l(
            "**一次能生成多条轨道的动画吗？** 可以。在 `轨道选择` 里勾多条再点一次 `✅ 添加选中的轨道`，输出文件名里的轨道号会体现这次的选择。",
            "**Can I animate several tracks at once?** Yes. Tick multiple rows in `轨道选择` / Track Selection and click `✅ 添加选中的轨道` / Add Selected Tracks once; the track part of the output file names reflects the selection.",
          ),
        },
        {
          kind: "p",
          text: l(
            "**点了 `🔍 扫描 MIDI 信息`，右栏还是 `暂无轨道信息`？** 说明还没选文件，或者这份 MIDI 里确实没有含音符的轨道。选好文件再扫一次；仍然为空就换一份 MIDI。",
            "**I clicked `🔍 扫描 MIDI 信息` and the right column still says `暂无轨道信息` / No track info.** Either no file is selected yet, or the MIDI genuinely has no tracks with notes. Pick a file and scan again; if it stays empty, try another MIDI.",
          ),
        },
        {
          kind: "p",
          text: l(
            "**生成很慢是正常的吗？** 取决于音符数量和 `FPS`。控制台会持续刷新「进度: 已处理/总数, 速度: N steps/s」，照着这个速度就能估算还要等多久。",
            "**Is it normal for generation to take a while?** It depends on the note count and `FPS`. The console keeps refreshing a progress line (done / total, steps per second), so you can estimate the remaining time from it.",
          ),
        },
        {
          kind: "p",
          text: l(
            "**动作看着不自然、手指像穿过去了？** 先用 `📋 记录查看` 定位到出问题的那一帧，再回角色页调动作参数。具体该动哪一个，见[软件界面详细说明](/details/key-ripple.html)。",
            "**The motion looks unnatural, or fingers seem to clip through each other.** Use `📋 记录查看` / Records to find the frame where it happens, then go back to the performer tab and adjust the motion parameters. Which one to change is covered in the [UI reference](/details/key-ripple.html).",
          ),
        },
        {
          kind: "p",
          text: l(
            "**界面语言和明暗主题在哪换？** 右上角有 `语言` 菜单（可以 `跟随系统`）和 `浅色` / `深色` 切换，选完会记住。",
            "**Where do I change the UI language and theme?** The top-right corner has a `语言` / Language menu (including `跟随系统` / Follow system) and `浅色` / Light and `深色` / Dark theme switches, and your choice is remembered.",
          ),
        },
      ],
    },
  ],
};
